/**
 * Process-wide "shutting down" flag set on SIGINT/SIGTERM (and when closing the bridge).
 * SQLite and other subsystems consult this to bail out of retry loops and long waits immediately.
 */

let cliImmediateShutdownRequested = false;
let symbolIndexShutdownPromise: Promise<void> | null = null;

/** Set once (idempotent). Call from signal handlers and bridge `close()` as early as possible. */
export function requestCliImmediateShutdown(): void {
  if (cliImmediateShutdownRequested) return;
  cliImmediateShutdownRequested = true;
  symbolIndexShutdownPromise = import('../code-nav/symbol-index/worker/lifecycle.js')
    .then(({ terminateSymbolIndexWorker }) => terminateSymbolIndexWorker())
    .catch(() => {
      /* worker module may not be loaded yet */
    });
}

export function isCliImmediateShutdownRequested(): boolean {
  return cliImmediateShutdownRequested;
}

/** Wait for symbol index worker shutdown and code-nav SQLite lock cleanup. */
export async function awaitSymbolIndexWorkerShutdown(): Promise<void> {
  if (symbolIndexShutdownPromise != null) {
    await symbolIndexShutdownPromise;
    return;
  }
  if (cliImmediateShutdownRequested) {
    const { terminateSymbolIndexWorker } = await import('../code-nav/symbol-index/worker/lifecycle.js');
    await terminateSymbolIndexWorker();
  }
}

/** Vitest / teardown: clear interrupt state for a clean worker. */
export function resetCliImmediateShutdownForTests(): void {
  cliImmediateShutdownRequested = false;
  symbolIndexShutdownPromise = null;
}

/**
 * Sleep up to `ms`, checking shutdown every ~50ms. Resolves `false` when interrupted, `true` when the full delay elapsed.
 */
export async function delayMsUnlessShutdownRequested(ms: number): Promise<boolean> {
  if (ms <= 0) return true;
  const end = Date.now() + ms;
  while (Date.now() < end) {
    if (isCliImmediateShutdownRequested()) return false;
    const chunk = Math.min(50, end - Date.now());
    if (chunk <= 0) break;
    await new Promise<void>((r) => setTimeout(r, chunk));
  }
  return !isCliImmediateShutdownRequested();
}
